import React from 'react';
import { motion } from 'framer-motion';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { FiMonitor, FiTruck, FiHeart } from 'react-icons/fi';
import { BiAtom, BiDumbbell, BiFoodMenu, BiLibrary } from 'react-icons/bi';

const facilities = [
  { icon: FiMonitor,  title: 'Smart Classrooms', description: 'Interactive whiteboards and digital learning tools in every classroom.' },
  { icon: BiAtom,     title: 'Science Labs',     description: 'Well-equipped Physics, Chemistry, and Biology laboratories for hands-on learning.' },
  { icon: FiMonitor,  title: 'Computer Lab',     description: 'Modern computer lab with high-speed internet for digital literacy.' },
  { icon: BiLibrary,  title: 'Library',          description: 'Extensive collection of books, journals, and digital resources.' },
  { icon: BiDumbbell, title: 'Sports Complex',   description: 'Multi-sport facilities including basketball, volleyball, and athletics track.' },
  { icon: FiTruck,    title: 'Transportation',   description: 'Safe and reliable school bus services covering all major routes.' },
  { icon: BiFoodMenu, title: 'Cafeteria',        description: 'Hygienic cafeteria serving nutritious meals and healthy snacks.' },
  { icon: FiHeart,    title: 'Medical Room',     description: 'On-campus medical facility with trained nurse and first aid support.' },
];

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

export default function FacilitiesSection() {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <section className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
        <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={isVisible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
          className="mb-16 flex flex-col sm:flex-row justify-between items-end gap-8">
          <div>
            <SectionLabel text="World-Class Facilities" />
            <h2 className="text-4xl sm:text-5xl font-bold text-[#0F172A] leading-tight">
              State-of-the-Art <span className="text-[#2563EB]">Infrastructure</span>
            </h2>
          </div>
          <p className="text-slate-500 max-w-md text-sm sm:text-base mb-2">
            We provide excellent facilities to create an optimal learning environment
            that supports academic and personal growth.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {facilities.map((facility, idx) => (
            <motion.div key={idx}
              initial={{ opacity: 0, y: 20 }} animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.45, delay: idx * 0.07 }}
              className="bg-[#F0F4FF] border border-blue-100 rounded-2xl p-7 group hover:bg-[#2563EB] hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-5 shadow-sm group-hover:bg-white/20 transition-colors">
                <facility.icon className="w-6 h-6 text-[#2563EB] group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-bold text-[#0F172A] group-hover:text-white text-base mb-2 transition-colors">{facility.title}</h3>
              <p className="text-slate-500 group-hover:text-blue-100/80 text-sm leading-relaxed transition-colors hidden sm:block">{facility.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
